import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Calculator, BookOpen, TrendingUp, Shield, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';

const Resources = () => {
  const { t } = useTranslation();
  
  const blogPosts = [
    {
      title: t('resources.post_1_title'),
      excerpt: t('resources.post_1_excerpt'),
      date: "2025-01-15",
      category: t('resources.post_1_category'),
      icon: TrendingUp,
    },
    {
      title: t('resources.post_2_title'),
      excerpt: t('resources.post_2_excerpt'),
      date: "2025-01-10",
      category: t('resources.post_2_category'),
      icon: Shield,
    },
    {
      title: t('resources.post_3_title'),
      excerpt: t('resources.post_3_excerpt'),
      date: "2025-01-05",
      category: t('resources.post_3_category'),
      icon: Calculator,
    },
  ];

  const resources = [
    {
      icon: FileText,
      title: t('resources.whitepaper_title'),
      description: t('resources.whitepaper_desc'),
      cta: t('resources.whitepaper_cta'),
      gradient: "from-gradient-pink/20 to-gradient-purple/20",
    },
    {
      icon: Calculator,
      title: t('resources.calculator_title'),
      description: t('resources.calculator_desc'),
      cta: t('resources.calculator_cta'),
      gradient: "from-gradient-purple/20 to-gradient-blue/20",
    },
    {
      icon: BookOpen,
      title: t('resources.casestudies_title'),
      description: t('resources.casestudies_desc'),
      cta: t('resources.casestudies_cta'),
      gradient: "from-gradient-blue/20 to-gradient-cyan/20",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-shadow-glow">
              {t('resources.title')}
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              {t('resources.subtitle')}
            </p>
          </motion.div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8">{t('resources.blog_title')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 card-dark hover:glow-effect transition-all duration-300 h-full">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gradient-pink/20 to-gradient-cyan/20 flex items-center justify-center mb-4">
                      <post.icon className="w-6 h-6 text-accent" />
                    </div>
                    <div className="text-sm text-accent mb-2">{post.category} • {post.date}</div>
                    <h3 className="text-xl font-bold mb-3">{post.title}</h3>
                    <p className="text-foreground/70 mb-4">{post.excerpt}</p>
                    <Button variant="link" className="text-accent p-0 hover:text-accent/80">
                      {t('resources.read_more')} →
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold mb-8">{t('resources.downloadable_title')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {resources.map((resource, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`p-8 card-dark gradient-border hover:glow-effect transition-all duration-300 h-full bg-gradient-to-br ${resource.gradient}`}>
                    <div className="w-14 h-14 rounded-lg gradient-brand flex items-center justify-center mb-6">
                      <resource.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{resource.title}</h3>
                    <p className="text-foreground/70 mb-6">{resource.description}</p>
                    <Button className="w-full gradient-brand text-white hover:opacity-90">
                      {resource.cta}
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          <Card className="mt-16 p-12 card-dark gradient-border text-center glow-effect">
            <Zap className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">{t('resources.newsletter_title')}</h2>
            <p className="text-foreground/70 mb-6 max-w-2xl mx-auto">
              {t('resources.newsletter_desc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t('resources.newsletter_placeholder')}
                className="flex-grow px-4 py-3 rounded-lg border border-input bg-card text-foreground focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
              />
              <Button className="gradient-brand text-white hover:opacity-90">
                {t('resources.newsletter_button')}
              </Button>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Resources;
